/**
 * CalibrationTestResetAdmin — put one tester's personas back to before their check-ins and calibration,
 * on a test environment (Victor, 14 Oct 2026; the Annual I&G Review card's pattern).
 *
 * Testers rehearse check-ins and calibration with their own personas, then start again. For the
 * tester chosen, their personas' own check-ins of the year go back to Pending and the calibration
 * sessions those personas chair or are in are cancelled, the way Cancel does it on screen: a completed
 * one gives back what it published. A copy of everything changed is kept. The server allows it only for
 * a signed-in admin or HR admin, never in production, and refuses it whole when a session also holds
 * someone who is not one of the tester's personas.
 */
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, CalendarClock, CheckCircle2, Loader2, RotateCcw } from 'lucide-react'
import toast from 'react-hot-toast'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { calibrationService } from '@/services/api/calibrationService'
import type { CalibrationTestResetResult } from '@/services/api/calibrationService'
import { useConfirm } from '@/hooks/useConfirm'

const plural = (count: number, one: string, many = `${one}s`) => `${count} ${count === 1 ? one : many}`

const refusal = (error: unknown, action: string): string => {
  switch ((error as { status?: number } | undefined)?.status) {
    case 401:
      return 'Your sign-in has expired. Sign in again and retry.'
    case 403:
      return "Only admin and HR admin can reset a tester's check-ins and calibration."
    case 404:
      return "Resetting a tester's check-ins and calibration is not available in this environment."
    case 409:
      return 'Nothing was reset: a calibration session also holds people who are not this tester’s personas. Cancel or change it on screen first.'
    case 503:
      return 'Your sign-in could not be checked just now. Try again in a moment.'
    default:
      return `The check-ins and calibration could not be ${action}. Try again in a moment.`
  }
}

const label = (value: string) => {
  const words = value.replace(/[_-]/g, ' ').toLowerCase()
  return words.charAt(0).toUpperCase() + words.slice(1)
}

/** UAT-EMP- / UAT-MGR- / UAT-ONEUP- in the screens' words. */
const personaRole = (persona: string) =>
  persona.startsWith('UAT-ONEUP-') ? 'One-Up' : persona.startsWith('UAT-MGR-') ? 'People Leader' : 'Team Member'

const ROWS: Record<string, [string, string?]> = {
  kpa_ratings: ['KPA rating'],
  self_reflections: ['self-reflection'],
  kpa_reflections: ['KPA reflection'],
  performance_rating_history: ['rating-history row'],
  calibration_remediation_log: ['entry recorded by HR', 'entries recorded by HR'],
}

const rowsLine = (rows: Record<string, number>) =>
  Object.entries(ROWS)
    .map(([table, [one, many]]) => plural(rows[table] ?? 0, one, many))
    .join(', ')

export default function CalibrationTestResetAdmin() {
  const [tester, setTester] = useState<string>('')
  const [year, setYear] = useState<number | undefined>(undefined)
  const [result, setResult] = useState<{ at: Date; data: CalibrationTestResetResult } | null>(null)
  const { confirm } = useConfirm()
  const queryClient = useQueryClient()

  const { data: preview, error, isPlaceholderData } = useQuery({
    queryKey: ['calibration', 'test-reset', tester, year],
    queryFn: () => calibrationService.getTestResetPreview(tester || undefined, year),
    // Keep the tester list on screen while another tester's plan loads.
    placeholderData: (previous) => previous,
    retry: false,
  })
  const testers = preview?.testers ?? []
  const reviewYear = year ?? preview?.review_year
  const plan = tester && !isPlaceholderData ? preview?.plan ?? null : null

  const reset = useMutation({
    mutationFn: () => calibrationService.resetTestData(tester, reviewYear),
    onSuccess: (data) => {
      setResult({ at: new Date(), data })
      // The calibration tab, the team page and the check-ins all read these.
      queryClient.invalidateQueries({ queryKey: ['calibration'] })
      queryClient.invalidateQueries({ queryKey: ['reviews'] })
      toast.success("The tester's check-ins and calibration have been reset")
    },
    onError: (err) => toast.error(refusal(err, 'reset')),
  })

  const startReset = async () => {
    if (!plan) return
    const invites = plan.sessions.filter((s) => s.calendar_invite).length
    const proceed = await confirm(
      `This puts ${plural(plan.check_ins.length, 'check-in')} of ${plan.participant.name || plan.participant.id}'s ` +
        `personas for ${plan.review_year} back to Pending and cancels ${plural(plan.sessions.length, 'calibration session')}, ` +
        'giving back anything a completed one published. A copy of everything changed is kept.' +
        (invites ? ` ${plural(invites, 'calendar invite')} already sent will stay in Outlook until cancelled there.` : ''),
      "Reset this tester's check-ins and calibration?",
      { confirmText: 'Reset', type: 'danger' },
    )
    if (proceed) reset.mutate()
  }

  const done = result?.data
  const doneInvites = done ? done.sessions.filter((s) => s.calendar_invite).length : 0
  const years = preview ? [preview.review_year, preview.review_year - 1] : []

  return (
    <div className="space-y-4" data-demo="admin-calibration-reset">
      <Card className="p-5">
        <div className="flex items-start gap-3">
          <div className="rounded-lg bg-heritage-blue/10 p-2">
            <RotateCcw className="h-5 w-5 text-heritage-blue" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-semibold">Check-ins and calibration</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Put one tester&apos;s personas back to before their check-ins. Their own check-ins of the year go back
              to Pending, and the calibration sessions they chair or are in are cancelled, giving back anything a
              completed one published. A copy of everything changed is kept.
            </p>
          </div>
        </div>

        <div className="mt-5 grid gap-3 sm:grid-cols-[minmax(0,1fr)_10rem_auto] sm:items-end">
          <div className="space-y-1.5">
            <Label htmlFor="calibration-reset-tester">Tester</Label>
            <Select value={tester} onValueChange={(v) => { setTester(v); setResult(null) }} disabled={reset.isPending}>
              <SelectTrigger id="calibration-reset-tester">
                <SelectValue placeholder="Choose a tester" />
              </SelectTrigger>
              <SelectContent>
                {testers.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.name} · {t.id}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="calibration-reset-year">Year</Label>
            <Select
              value={reviewYear ? String(reviewYear) : ''}
              onValueChange={(v) => setYear(Number(v))}
              disabled={reset.isPending || !preview}
            >
              <SelectTrigger id="calibration-reset-year">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {years.map((y) => (
                  <SelectItem key={y} value={String(y)}>
                    {y}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            variant="destructive"
            onClick={startReset}
            disabled={!plan || !plan.can_reset || !!error || reset.isPending}
            data-demo="admin-calibration-reset-run"
          >
            {reset.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RotateCcw className="mr-2 h-4 w-4" />}
            {reset.isPending ? 'Resetting…' : 'Reset'}
          </Button>
        </div>

        {error ? (
          <p className="mt-4 text-sm text-red-600">{refusal(error, 'checked')}</p>
        ) : !preview || (tester && !plan) ? (
          <p className="mt-4 flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Checking…
          </p>
        ) : !plan ? (
          <p className="mt-4 text-sm text-muted-foreground">Choose a tester to see what a reset would change.</p>
        ) : (
          <div className="mt-4 space-y-3" data-demo="admin-calibration-reset-preview">
            {!plan.can_reset && (
              <p className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                {plural(plan.refused_sessions.length, 'calibration session')} below also{' '}
                {plan.refused_sessions.length === 1 ? 'holds' : 'hold'} people who are not this tester&apos;s
                personas, so nothing can be reset. Cancel or change {plan.refused_sessions.length === 1 ? 'it' : 'them'} on
                screen first.
              </p>
            )}
            <div>
              <p className="text-sm font-medium">
                {plural(plan.check_ins.length, 'check-in')} back to Pending
              </p>
              {plan.check_ins.length > 0 && (
                <ul className="mt-1 divide-y rounded-md border">
                  {plan.check_ins.map((c) => (
                    <li key={c.id} className="flex flex-wrap items-center justify-between gap-2 p-2.5 text-sm">
                      <span>
                        {label(c.review_type)} · {personaRole(c.persona)} ({c.persona})
                      </span>
                      <span className="rounded-full bg-muted px-2 py-0.5 text-xs">{label(c.status)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div>
              <p className="text-sm font-medium">{plural(plan.sessions.length, 'calibration session')} cancelled</p>
              {plan.sessions.length > 0 && (
                <ul className="mt-1 divide-y rounded-md border">
                  {plan.sessions.map((s) => {
                    const held = s.others.check_ins + s.others.submissions
                    return (
                      <li key={s.id} className="flex flex-wrap items-start justify-between gap-2 p-2.5 text-sm">
                        <div className="min-w-0">
                          <p className="font-medium">
                            {label(s.type)} {s.review_year} · chaired by {s.chair_id || 'nobody'}
                          </p>
                          {held > 0 && (
                            <p className="text-xs text-red-700">
                              Also holds {plural(held, 'check-in or submission', 'check-ins or submissions')} of other
                              people: not cancelled, and nothing is reset.
                            </p>
                          )}
                          {s.others.uncovered_check_ins > 0 && (
                            <p className="text-xs text-red-700">
                              Also holds {plural(s.others.uncovered_check_ins, 'check-in')} from another year: not
                              cancelled, and nothing is reset.
                            </p>
                          )}
                          {s.others.seated.length > 0 && (
                            <p className="text-xs text-muted-foreground">
                              Also seated: {s.others.seated.join(', ')}. The session leaves their list too.
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {s.calendar_invite && (
                            <span className="inline-flex items-center gap-1 text-xs text-amber-700">
                              <CalendarClock className="h-3.5 w-3.5" /> Invite sent
                            </span>
                          )}
                          <span className="rounded-full bg-muted px-2 py-0.5 text-xs">{label(s.status)}</span>
                        </div>
                      </li>
                    )
                  })}
                </ul>
              )}
            </div>
            <p className="text-xs text-muted-foreground">Also removed from those check-ins: {rowsLine(plan.rows)}.</p>
          </div>
        )}
      </Card>

      {result && done && (
        <Card className="p-5" data-demo="admin-calibration-reset-result">
          <div className="flex flex-wrap items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-green-600" />
            <h3 className="font-semibold">Reset for {done.participant.name || done.participant.id}</h3>
            <span className="text-xs text-muted-foreground">
              {result.at.toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
          <dl className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
            {[
              { label: 'Check-ins back to Pending', value: done.check_ins.length },
              { label: 'Calibration sessions cancelled', value: done.sessions_cancelled },
              { label: 'Published ratings given back', value: done.check_ins_given_back },
            ].map((c) => (
              <div key={c.label} className="rounded-md border p-3">
                <dt className="text-xs text-muted-foreground">{c.label}</dt>
                <dd className="text-xl font-semibold">{c.value}</dd>
              </div>
            ))}
          </dl>
          <ul className="mt-3 space-y-1 text-sm text-muted-foreground">
            <li>Removed: {rowsLine(done.removed)}.</li>
            {done.backup_prefix && <li>A copy of what changed is in the performance.{done.backup_prefix}* tables.</li>}
            {doneInvites > 0 && <li>{plural(doneInvites, 'calendar invite')} already sent stay in Outlook until cancelled there.</li>}
          </ul>
        </Card>
      )}
    </div>
  )
}
